import EasyTooltip from "@/src/easy-ui/components/EasyTooltip.vue";
import isString from "lodash/isString";
import { useDirectiveComponent } from "@/src/easy-ui/composables/directiveComponent";
import { DirectiveBinding } from "vue";
import { EasyTooltipProps, EasyTooltipValue } from "@/src/easy-ui/types/easyTooltip";

/**
 * Checks if tooltip value is valid
 */
const isValidValue = (value?: null | EasyTooltipValue): boolean => {
  return !!value && !(typeof value === "object" && !value.text);
};

/**
 * Formats the tooltip location from the directive argument
 */
const getLocation = (arg: string | undefined, value: EasyTooltipValue): string | undefined => {
  return arg?.replace("-", " ") || (typeof value === "object" ? value.location : undefined);
};

/**
 * Creates props for the tooltip component
 */
const createTooltipProps = (el: HTMLElement, value: EasyTooltipValue, location?: string): EasyTooltipProps => {
  const props: EasyTooltipProps = { activator: el, location };
  return isString(value) ? { ...props, text: value } : { ...props, ...value };
};

/**
 * Custom directive for EasyTooltip component.
 *
 * @example
 * <div v-easy-tooltip="'Tooltip text'"></div>
 * <div v-easy-tooltip:top="'Tooltip text'"></div>
 * <div v-easy-tooltip="{ text: 'Tooltip text' }"></div>
 */
export const vEasyTooltip = useDirectiveComponent(EasyTooltip, (binding: DirectiveBinding, el: HTMLElement) => {
  const { arg, value } = binding;

  if (!isValidValue(value)) {
    return null;
  }

  const location = getLocation(arg, value as EasyTooltipValue);
  return createTooltipProps(el, value as EasyTooltipValue, location);
});
